import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, Star, Upload, Download, Loader2, X, Image as ImageIcon, Sparkles, MessageSquarePlus, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import AdminBreadcrumb from '@/components/AdminBreadcrumb';
import KeepsakeCardsSection from '@/components/KeepsakeCardsSection';
import ReviewsDialog from '@/components/ReviewsDialog';
import SnapshotStatusBar from '@/components/SnapshotStatusBar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import logger from '@/utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyProduct = {
  id: '',
  name: '',
  description: '',
  inspiration: '',
  product_description: '',
  gift_message: '',
  price: 0,
  morse_text: '',
  category: 'bestseller',
  image: '',
  images: [],
  occasions: [],
  featured: false,
  bead_color: 'gold',
  default_clasp: 'lobster',
  addon_product_ids: [],
  show_addons: true,
  show_keepsake_cards: true,
};

const BEAD_COLOR_OPTIONS = [
  { id: 'gold', label: 'Warm gold' },
  { id: 'silver', label: 'Sterling silver' },
  { id: 'roseGold', label: 'Rose gold' },
];

const CLASP_OPTIONS = [
  { id: 'lobster', label: 'Lobster claw' },
  { id: 'drawstring', label: 'Cotton drawstring' },
  { id: 'magnetic-heart', label: 'Magnetic heart' },
];

const categories = [
  { id: 'bestseller', label: 'Bestseller' },
  { id: 'collection', label: 'Collection' },
  { id: 'custom', label: 'Custom' },
  { id: 'bracelet', label: 'Bracelet' },
  { id: 'pocket-stone', label: 'Pocket stone' },
];

const OCCASION_OPTIONS = [
  { id: 'weddings', label: 'Wedding Gifts' },
  { id: 'baby-showers', label: 'Baby Showers' },
  { id: 'in-loving-memory', label: 'In Loving Memory' },
  { id: 'birthdays', label: 'Birthdays' },
  { id: 'friends', label: 'For Friends' },
  { id: 'holidays', label: 'Holidays' },
];

const generateId = (name) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

export default function ProductAdminPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(emptyProduct);
  const [isNewProduct, setIsNewProduct] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generatingAi, setGeneratingAi] = useState(false);
  const [generatingLifestyle, setGeneratingLifestyle] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [aiImageIds, setAiImageIds] = useState({});  // { gridfsId: kind }
  const [reviewCounts, setReviewCounts] = useState({});  // { productId: {real, seeded, total} }
  const [seedingReviews, setSeedingReviews] = useState({});  // { productId: true }
  const [reviewsDialogProduct, setReviewsDialogProduct] = useState(null);  // product whose reviews are being managed
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const resolveImageUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('/api/')) return `${BACKEND_URL}${img}`;
    return img;
  };

  // Returns the AI "kind" for a GridFS-backed URL, or null if it's a real
  // photo (external URL, or an upload that wasn't tagged by the AI pipeline).
  const aiKindForImage = (img) => {
    if (!img || !img.startsWith('/api/uploads/')) return null;
    const id = img.replace('/api/uploads/', '').split('?')[0];
    return aiImageIds[id] || null;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image too large (max 8 MB)');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploading(true);
      const response = await axios.post(`${API}/uploads`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCurrentProduct((prev) => ({ ...prev, image: response.data.url }));
      toast.success('Image uploaded');
    } catch (error) {
      logger.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name}: not an image`);
          continue;
        }
        if (file.size > 8 * 1024 * 1024) {
          toast.error(`${file.name}: too large (max 8 MB)`);
          continue;
        }
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/uploads`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push(response.data.url);
      }
      if (uploaded.length > 0) {
        setCurrentProduct((prev) => ({
          ...prev,
          images: [...(prev.images || []), ...uploaded],
        }));
        toast.success(`${uploaded.length} image${uploaded.length > 1 ? 's' : ''} added`);
      }
    } catch (error) {
      logger.error('Gallery upload failed:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const removeGalleryImage = (idx) => {
    setCurrentProduct((prev) => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== idx),
    }));
  };

  const promoteToCover = (idx) => {
    setCurrentProduct((prev) => {
      const extras = [...(prev.images || [])];
      const newCover = extras.splice(idx, 1)[0];
      const oldCover = prev.image;
      return {
        ...prev,
        image: newCover,
        images: oldCover ? [oldCover, ...extras] : extras,
      };
    });
    toast.success('Cover image updated');
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      logger.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchAiImageIds = async () => {
    try {
      const response = await axios.get(`${API}/uploads-ai/ids`);
      setAiImageIds(response.data?.ai_ids || {});
    } catch (error) {
      logger.error('Could not fetch AI image ids:', error);
    }
  };

  const fetchReviewCounts = async () => {
    try {
      const response = await axios.get(`${API}/reviews/counts`);
      setReviewCounts(response.data || {});
    } catch (error) {
      logger.error('Could not fetch review counts:', error);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [productsRes, aiRes, countsRes] = await Promise.all([
          axios.get(`${API}/products`),
          axios.get(`${API}/uploads-ai/ids`),
          axios.get(`${API}/reviews/counts`),
        ]);
        if (cancelled) return;
        setProducts(productsRes.data);
        setAiImageIds(aiRes.data?.ai_ids || {});
        setReviewCounts(countsRes.data || {});
      } catch (error) {
        if (cancelled) return;
        logger.error('Error fetching products:', error);
        toast.error('Failed to load products');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSeedReviews = async (product) => {
    const existing = reviewCounts[product.id] || { seeded: 0, real: 0 };
    if (existing.seeded > 0) {
      const ok = window.confirm(
        `This product already has ${existing.seeded} AI-seeded review${existing.seeded > 1 ? 's' : ''}. ` +
          'Regenerate? (Real customer reviews are not touched.)',
      );
      if (!ok) return;
    }
    setSeedingReviews((prev) => ({ ...prev, [product.id]: true }));
    try {
      const response = await axios.post(
        `${API}/products/${product.id}/seed-reviews`,
        {},
        { timeout: 120000 },
      );
      if (response.data?.ok) {
        toast.success(`${response.data.added} review${response.data.added > 1 ? 's' : ''} generated`);
        fetchReviewCounts();
      } else {
        toast.error(response.data?.detail || 'Could not generate reviews');
      }
    } catch (error) {
      logger.error('Review seed failed:', error);
      toast.error(error.response?.data?.detail || 'Review seed failed');
    } finally {
      setSeedingReviews((prev) => {
        const next = { ...prev };
        delete next[product.id];
        return next;
      });
    }
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    const updates = { name };
    if (isNewProduct) {
      updates.id = generateId(name);
      if (!currentProduct.morse_text) {
        updates.morse_text = name.replace(/[^A-Za-z\s]/g, '').toUpperCase().trim();
      }
    }
    setCurrentProduct({ ...currentProduct, ...updates });
  };

  const handleCreateNew = () => {
    setCurrentProduct(emptyProduct);
    setIsNewProduct(true);
    setIsEditing(true);
  };

  // Export current catalogue as a Gemini-friendly JSON file (round-trip with /admin/products/bulk-import)
  const handleDownloadJson = () => {
    if (products.length === 0) return;
    const exportable = products.map((p) => ({
      id: p.id,
      name: p.name,
      price: p.price,
      category: p.category,
      occasions: p.occasions || [],
      image: p.image || '',
      images: p.images || [],
      morse_text: p.morse_text || '',
      description: p.description || '',
      inspiration: p.inspiration || '',
      product_description: p.product_description || '',
      gift_message: p.gift_message || '',
      featured: !!p.featured,
      bead_color: p.bead_color || 'gold',
      clasp: p.default_clasp || 'lobster',
    }));
    const blob = new Blob([JSON.stringify(exportable, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `whisperingwristwear-products-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
    toast.success(`Exported ${products.length} product${products.length === 1 ? '' : 's'}.`);
  };

  const handleEdit = (product) => {
    setCurrentProduct({
      ...product,
      images: product.images || [],
      occasions: product.occasions || [],
      bead_color: product.bead_color || 'gold',
      default_clasp: product.default_clasp || 'lobster',
      // `addon_product_ids` is the source of truth. The legacy
      // `pocket_stone_ids` field is intentionally NOT read as a fallback
      // anymore — falling back to it was resurrecting stale data whenever
      // a user cleared all addons on the live site.
      addon_product_ids: product.addon_product_ids || [],
      // Mirror the empty state into pocket_stone_ids too so a subsequent
      // full-product PUT never re-writes stale legacy data back.
      pocket_stone_ids: product.addon_product_ids || [],
      show_addons:
        product.show_addons === undefined ? true : !!product.show_addons,
      show_keepsake_cards:
        product.show_keepsake_cards === undefined
          ? true
          : !!product.show_keepsake_cards,
    });
    setIsNewProduct(false);
    setIsEditing(true);
  };

  // Generate a fresh AI photo using the product's saved bead_color + default_clasp.
  // The new image becomes the cover; the old cover (if any) drops into the gallery
  // so nothing is lost. Works for both new and existing products.
  const handleGenerateAiPhoto = async () => {
    const sourceWord = (currentProduct.morse_text || currentProduct.name || '').trim();
    if (!sourceWord) {
      toast.error('Add a name or Morse word first');
      return;
    }
    try {
      setGeneratingAi(true);
      const response = await axios.post(`${API}/builder/render`, {
        text: sourceWord,
        bead_color: currentProduct.bead_color || 'gold',
        size_cm: 19.0,
        clasp: currentProduct.default_clasp || 'lobster',
      });
      const newUrl = response.data?.image_url;
      if (!newUrl) throw new Error('No image returned');
      setCurrentProduct((prev) => {
        const previousCover = prev.image;
        const galleryExtras = prev.images || [];
        const nextGallery = previousCover && !galleryExtras.includes(previousCover)
          ? [previousCover, ...galleryExtras]
          : galleryExtras;
        return { ...prev, image: newUrl, images: nextGallery };
      });
      toast.success('AI photo ready — old cover moved to the gallery');
      fetchAiImageIds();
    } catch (error) {
      logger.error('AI variant render failed:', error);
      toast.error(error.response?.data?.detail || 'AI photo generation failed');
    } finally {
      setGeneratingAi(false);
    }
  };

  // Generate the 2 lifestyle photos (woman wearing + showing friends in Cyprus)
  // for the product currently being edited and append them to its gallery.
  const handleGenerateLifestylePair = async () => {
    if (!currentProduct.id || isNewProduct) {
      toast.error('Save the product first, then add lifestyle photos.');
      return;
    }
    try {
      setGeneratingLifestyle(true);
      const response = await axios.post(
        `${API}/products/${currentProduct.id}/lifestyle-photos`,
        {},
        { timeout: 180000 },
      );
      const product = response.data?.product;
      const added = response.data?.result?.added || 0;
      if (product) {
        setCurrentProduct((prev) => ({
          ...prev,
          image: product.image || prev.image,
          images: product.images || prev.images || [],
        }));
      }
      if (added > 0) {
        toast.success(`${added} lifestyle photo${added > 1 ? 's' : ''} added to gallery`);
        fetchProducts();
        fetchAiImageIds();
      } else {
        toast.error('Lifestyle render failed — please try again');
      }
    } catch (error) {
      logger.error('Lifestyle render failed:', error);
      toast.error(error.response?.data?.detail || 'Lifestyle render failed');
    } finally {
      setGeneratingLifestyle(false);
    }
  };

  // One-click backfill: for every product currently showing only one photo,
  // generate the 2 lifestyle photos and append them.
  const handleBackfillLifestylePhotos = async () => {
    if (backfilling) return;
    const singleImageCount = products.filter((p) => {
      const set = new Set();
      if (p.image) set.add(p.image);
      (p.images || []).forEach((img) => img && set.add(img));
      return set.size <= 1;
    }).length;
    if (singleImageCount === 0) {
      toast.info('All products already have 2+ photos — nothing to backfill.');
      return;
    }
    const ok = window.confirm(
      `This will generate 2 AI lifestyle photos for ${singleImageCount} ` +
        `product${singleImageCount === 1 ? '' : 's'}. It may take a couple of minutes. Continue?`,
    );
    if (!ok) return;
    try {
      setBackfilling(true);
      toast.loading('Generating lifestyle photos…', { id: 'lifestyle-backfill' });
      const response = await axios.post(
        `${API}/products/lifestyle-backfill`,
        {},
        { timeout: 600000 },
      );
      const { succeeded = 0, failed = 0, processed = 0 } = response.data || {};
      toast.dismiss('lifestyle-backfill');
      if (succeeded > 0) {
        toast.success(`${succeeded}/${processed} products got lifestyle photos`);
      }
      if (failed > 0) {
        toast.error(`${failed} product${failed > 1 ? 's' : ''} failed — check logs`);
      }
      fetchProducts();
      fetchAiImageIds();
    } catch (error) {
      toast.dismiss('lifestyle-backfill');
      logger.error('Lifestyle backfill failed:', error);
      toast.error(error.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  const toggleOccasion = (occId) => {
    setCurrentProduct((prev) => {
      const current = prev.occasions || [];
      return {
        ...prev,
        occasions: current.includes(occId)
          ? current.filter((o) => o !== occId)
          : [...current, occId],
      };
    });
  };

  const handleSave = async () => {
    const { id, name, description, price, morse_text, image } = currentProduct;
    if (!id || !name || !description || !morse_text || !image || price === '' || price === null) {
      toast.error('Please fill in all required fields');
      return;
    }

    const addonIds = currentProduct.addon_product_ids || [];
    const payload = {
      ...currentProduct,
      price: parseFloat(currentProduct.price) || 0,
      // Force the legacy field to mirror addon_product_ids on every save so
      // stale legacy data can never resurrect after a full-product PUT.
      addon_product_ids: addonIds,
      pocket_stone_ids: addonIds,
    };

    try {
      setSaving(true);
      if (isNewProduct) {
        await axios.post(`${API}/products`, payload);
        toast.success('Product created!');
      } else {
        await axios.put(`${API}/products/${currentProduct.id}`, payload);
        toast.success('Product updated!');
      }
      setIsEditing(false);
      fetchProducts();
    } catch (error) {
      logger.error('Error saving product:', error);
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/products/${deleteId}`);
      toast.success('Product deleted');
      setDeleteId(null);
      fetchProducts();
    } catch (error) {
      logger.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  return (
    <div className="pt-28 pb-16 min-h-screen bg-[#F5F5F0]" data-testid="product-admin-page">
      <div className="container-custom px-6 md:px-12 lg:px-24">
        <AdminBreadcrumb current="Products" />
        <SnapshotStatusBar />
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <p className="text-caption text-[#84A98C] mb-2">Admin</p>
            <h1 className="heading-display text-3xl text-[#354F52]">Product Manager</h1>
            <p className="text-sm text-[#84A98C] mt-1">
              {products.length} {products.length === 1 ? 'product' : 'products'} in your shop
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
              type="button"
              onClick={handleBackfillLifestylePhotos}
              disabled={backfilling || products.length === 0}
              className="btn-secondary flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="backfill-lifestyle-btn"
              title="Adds 2 AI lifestyle photos to every product that only has 1 photo"
            >
              {backfilling ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                  Generating…
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                  Add lifestyle photos
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleDownloadJson}
              disabled={products.length === 0}
              className="btn-secondary flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="download-json-btn"
            >
              <Download className="w-4 h-4" strokeWidth={1.5} />
              Download JSON
            </button>
            <Link
              to="/admin/products/bulk-import"
              className="btn-secondary flex items-center justify-center gap-2"
              data-testid="bulk-import-link"
            >
              <Upload className="w-4 h-4" strokeWidth={1.5} />
              Bulk import
            </Link>
            <Button
              onClick={handleCreateNew}
              className="btn-primary flex items-center gap-2"
              data-testid="create-new-product-btn"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
              New Product
            </Button>
          </div>
        </div>

        {/* Products List */}
        <div className="bg-[#FAF9F6] rounded-sm shadow-soft">
          {loading ? (
            <div className="p-8 text-center text-[#84A98C]">Loading products...</div>
          ) : products.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-[#84A98C] mb-4">No products yet</p>
              <Button onClick={handleCreateNew} className="btn-secondary">
                Add Your First Product
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-[#CAD2C5]/30">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="p-6 flex flex-col md:flex-row md:items-center gap-4"
                  data-testid={`admin-product-${product.id}`}
                >
                  {/* Thumbnail */}
                  <div className="flex-shrink-0 relative">
                    <img
                      src={resolveImageUrl(product.image)}
                      alt={product.name}
                      className="w-20 h-20 object-cover rounded-sm bg-[#F5F5F0]"
                      onError={(e) => {
                        e.target.src =
                          'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><rect width="80" height="80" fill="%23CAD2C5"/></svg>';
                      }}
                    />
                    {aiKindForImage(product.image) && (
                      <span
                        className="absolute top-1 left-1 text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#354F52]/85 text-[#FAF9F6] flex items-center gap-0.5"
                        title="AI-generated cover image"
                        data-testid={`ai-badge-thumb-${product.id}`}
                      >
                        <Sparkles className="w-2.5 h-2.5" strokeWidth={2} />
                        AI
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-serif text-lg text-[#354F52] truncate">
                        {product.name}
                      </h3>
                      {product.featured && (
                        <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Star className="w-3 h-3" strokeWidth={2} />
                          Featured
                        </span>
                      )}
                      <span className="text-xs bg-[#84A98C]/20 text-[#52796F] px-2 py-0.5 rounded-full capitalize">
                        {product.category}
                      </span>
                    </div>
                    <p className="text-sm text-[#D4AF37] font-medium mb-1">
                      €{product.price.toFixed(2)}
                    </p>
                    <p className="text-xs text-[#84A98C] line-clamp-1 font-mono">
                      id: {product.id} · morse: {product.morse_text}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {(() => {
                      const counts = reviewCounts[product.id] || { seeded: 0, real: 0, total: 0 };
                      const isSeeding = !!seedingReviews[product.id];
                      const needsSeeding = counts.seeded === 0;
                      return (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSeedReviews(product)}
                          disabled={isSeeding}
                          className={
                            needsSeeding
                              ? 'text-[#D4AF37] hover:text-[#B8941F]'
                              : 'text-[#84A98C] hover:text-[#354F52]'
                          }
                          title={
                            needsSeeding
                              ? 'No AI reviews yet — click to generate 2 (UK + Cyprus)'
                              : `Regenerate AI reviews (currently ${counts.seeded} seeded · ${counts.real} real)`
                          }
                          data-testid={`seed-reviews-${product.id}`}
                        >
                          {isSeeding ? (
                            <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                          ) : (
                            <MessageSquarePlus className="w-4 h-4" strokeWidth={1.5} />
                          )}
                        </Button>
                      );
                    })()}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setReviewsDialogProduct(product)}
                      className="text-[#84A98C] hover:text-[#354F52]"
                      title={`Manage reviews (${(reviewCounts[product.id] || {}).total || 0})`}
                      data-testid={`manage-reviews-${product.id}`}
                    >
                      <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(`/products/${product.id}`, '_blank')}
                      className="text-[#84A98C] hover:text-[#354F52]"
                      data-testid={`view-${product.id}`}
                    >
                      <Eye className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(product)}
                      className="text-[#84A98C] hover:text-[#354F52]"
                      data-testid={`edit-${product.id}`}
                    >
                      <Edit className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteId(product.id)}
                      className="text-[#BC4749] hover:text-[#BC4749]/80"
                      data-testid={`delete-${product.id}`}
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Edit / Create Dialog */}
        <Dialog open={isEditing} onOpenChange={setIsEditing}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[#354F52]">
                {isNewProduct ? 'New Product' : 'Edit Product'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-5 mt-2">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#354F52]">
                  Name *
                </Label>
                <Input
                  id="name"
                  value={currentProduct.name}
                  onChange={handleNameChange}
                  placeholder="The 'Love' Bracelet"
                  data-testid="input-name"
                />
              </div>

              {/* ID + Morse text row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="id" className="text-[#354F52]">
                    Product ID (URL slug) *
                  </Label>
                  <Input
                    id="id"
                    value={currentProduct.id}
                    onChange={(e) =>
                      setCurrentProduct({ ...currentProduct, id: e.target.value })
                    }
                    disabled={!isNewProduct}
                    placeholder="love-bracelet"
                    data-testid="input-id"
                  />
                  <p className="text-xs text-[#84A98C]">
                    {isNewProduct ? 'Auto-generated, edit if needed' : 'Cannot be changed'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="morse_text" className="text-[#354F52]">
                    Morse Code Word *
                  </Label>
                  <Input
                    id="morse_text"
                    value={currentProduct.morse_text}
                    onChange={(e) =>
                      setCurrentProduct({
                        ...currentProduct,
                        morse_text: e.target.value.toUpperCase(),
                      })
                    }
                    placeholder="LOVE"
                    data-testid="input-morse"
                  />
                </div>
              </div>

              {/* Price + Category row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price" className="text-[#354F52]">
                    Price (€) *
                  </Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={currentProduct.price}
                    onChange={(e) =>
                      setCurrentProduct({ ...currentProduct, price: e.target.value })
                    }
                    placeholder="45.00"
                    data-testid="input-price"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category" className="text-[#354F52]">
                    Category *
                  </Label>
                  <Select
                    value={currentProduct.category}
                    onValueChange={(value) =>
                      setCurrentProduct({ ...currentProduct, category: value })
                    }
                  >
                    <SelectTrigger data-testid="select-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Image: upload + URL */}
              <div className="space-y-2">
                <Label htmlFor="image" className="text-[#354F52]">
                  Product Image *
                </Label>

                {/* Upload button */}
                <div className="flex items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-2"
                    data-testid="upload-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" strokeWidth={1.5} />
                        Upload from computer
                      </>
                    )}
                  </Button>
                  <span className="text-xs text-[#84A98C]">or paste a URL below</span>
                </div>

                {/* URL input */}
                <Input
                  id="image"
                  value={currentProduct.image}
                  onChange={(e) =>
                    setCurrentProduct({ ...currentProduct, image: e.target.value })
                  }
                  placeholder="https://... or /api/uploads/..."
                  data-testid="input-image"
                />
                {currentProduct.image && (
                  <div className="relative inline-block">
                    <img
                      src={resolveImageUrl(currentProduct.image)}
                      alt="preview"
                      className="mt-2 w-32 h-32 object-cover rounded-sm border border-[#CAD2C5]"
                    />
                    {aiKindForImage(currentProduct.image) && (
                      <span
                        className="absolute top-3 left-1 text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#354F52]/85 text-[#FAF9F6] flex items-center gap-0.5"
                        title={`AI-generated · ${aiKindForImage(currentProduct.image)}`}
                        data-testid="ai-badge-cover-preview"
                      >
                        <Sparkles className="w-2.5 h-2.5" strokeWidth={2} />
                        AI
                      </span>
                    )}
                  </div>
                )}
                <p className="text-xs text-[#84A98C]">
                  PNG, JPG, WebP or GIF · max 8 MB
                </p>
              </div>

              {/* AI Variant — bead colour + clasp + regenerate */}
              <div
                className="space-y-3 p-4 bg-[#F5F5F0] rounded-sm border border-[#CAD2C5]/40"
                data-testid="ai-variant-section"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Label className="text-[#354F52] flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                      AI variant photo
                    </Label>
                    <p className="text-xs text-[#84A98C] mt-1">
                      Pick the bead colour + clasp, then regenerate. The new image
                      becomes the cover; the previous cover moves to the gallery.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="bead_color" className="text-xs text-[#52796F]">
                      Bead colour
                    </Label>
                    <Select
                      value={currentProduct.bead_color || 'gold'}
                      onValueChange={(value) =>
                        setCurrentProduct({ ...currentProduct, bead_color: value })
                      }
                    >
                      <SelectTrigger data-testid="select-bead-color">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BEAD_COLOR_OPTIONS.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="default_clasp" className="text-xs text-[#52796F]">
                      Clasp
                    </Label>
                    <Select
                      value={currentProduct.default_clasp || 'lobster'}
                      onValueChange={(value) =>
                        setCurrentProduct({ ...currentProduct, default_clasp: value })
                      }
                    >
                      <SelectTrigger data-testid="select-default-clasp">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CLASP_OPTIONS.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerateAiPhoto}
                  disabled={generatingAi || generatingLifestyle || uploading}
                  className="flex items-center gap-2 w-full md:w-auto"
                  data-testid="generate-ai-photo-btn"
                >
                  {generatingAi ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                      Rendering with Nano Banana...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                      Generate AI photo for this variant
                    </>
                  )}
                </Button>

                {/* Cross-product add-ons — any product can pair with any
                    other (pocket stones, mini-bracelets, gift cards, etc). */}
                {(() => {
                  const otherProducts = products.filter(
                    (p) => p.id && p.id !== currentProduct.id,
                  );
                  const persistAddons = async (nextIds) => {
                    // Optimistic UI: reflect the toggle instantly, then fire
                    // a targeted PATCH so the change survives a page refresh
                    // without needing the operator to click "Save Product".
                    setCurrentProduct((prev) => ({
                      ...prev,
                      addon_product_ids: nextIds,
                    }));
                    // New products aren't in the DB yet — the addons are
                    // saved as part of the initial POST when the product is
                    // first created, so skip the PATCH.
                    if (isNewProduct || !currentProduct.id) return;
                    try {
                      await axios.patch(
                        `${API}/products/${currentProduct.id}/addons`,
                        { addon_product_ids: nextIds },
                      );
                      // Keep the products list in sync too so the picker
                      // reflects the change if this product itself is used as
                      // an add-on on another product.
                      setProducts((prev) =>
                        prev.map((p) =>
                          p.id === currentProduct.id
                            ? { ...p, addon_product_ids: nextIds }
                            : p,
                        ),
                      );
                    } catch (err) {
                      logger.error('Auto-save addons failed:', err);
                      toast.error('Could not save add-on change — please retry');
                      // Roll back the optimistic update so the UI matches DB.
                      setCurrentProduct((prev) => ({
                        ...prev,
                        addon_product_ids:
                          currentProduct.addon_product_ids || [],
                      }));
                    }
                  };
                  return (
                    <div className="pt-3 border-t border-[#CAD2C5]/40 space-y-3">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={currentProduct.show_addons !== false}
                          onCheckedChange={(v) =>
                            setCurrentProduct({
                              ...currentProduct,
                              show_addons: v,
                            })
                          }
                          data-testid="show-addons-toggle"
                        />
                        <Label className="text-[#354F52] cursor-pointer">
                          Show the &ldquo;Complete the intention&rdquo; add-ons on this product page
                        </Label>
                      </div>
                      <p className="text-xs text-[#84A98C]">
                        When off, the add-on cross-sell section is hidden from
                        customers on this product&rsquo;s page, no matter which
                        add-ons are ticked below. Turn off for anything that
                        shouldn&rsquo;t cross-sell (gift vouchers, print-only
                        cards, etc). Save Product to apply.
                      </p>
                      {currentProduct.show_addons !== false && (
                        <>
                          <Label className="text-[#354F52] pt-2 block">
                            Add-ons on this product page
                          </Label>
                          <p className="text-xs text-[#84A98C]">
                            Tick any product you want to feature on this page under
                            &ldquo;Complete the intention&rdquo;. Changes save automatically —
                            no need to click &ldquo;Save Product&rdquo; for this section.
                          </p>
                      <div
                        className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto p-1 rounded-sm"
                        data-testid="pocket-stones-picker"
                      >
                        {otherProducts.map((addon) => {
                          const ids = currentProduct.addon_product_ids || [];
                          const checked = ids.includes(addon.id);
                          return (
                            <label
                              key={addon.id}
                              className={`flex items-center gap-3 p-2 border rounded-sm cursor-pointer transition-colors ${
                                checked
                                  ? 'border-[#D4AF37] bg-[#FAF9F6]'
                                  : 'border-[#CAD2C5]/60 hover:border-[#84A98C]'
                              }`}
                              data-testid={`pocket-stone-toggle-${addon.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...ids, addon.id]
                                    : ids.filter((id) => id !== addon.id);
                                  persistAddons(next);
                                }}
                                className="w-4 h-4 accent-[#D4AF37]"
                              />
                              <span className="text-sm text-[#354F52] flex-1 truncate">
                                {addon.name}
                              </span>
                              <span className="text-[10px] uppercase tracking-widest text-[#84A98C] mr-2">
                                {addon.category}
                              </span>
                              <span className="text-xs text-[#84A98C] whitespace-nowrap">
                                €{(addon.price || 0).toFixed(2)}
                              </span>
                            </label>
                          );
                        })}
                        {otherProducts.length === 0 && (
                          <p className="text-xs text-[#84A98C] italic col-span-full">
                            No other products in your catalogue yet.
                          </p>
                        )}
                      </div>
                      {(currentProduct.addon_product_ids || []).length > 0 && (
                        <p className="text-xs text-[#52796F]">
                          {currentProduct.addon_product_ids.length} add-on
                          {currentProduct.addon_product_ids.length === 1 ? '' : 's'}{' '}
                          selected. They&rsquo;ll appear in the order you tick them.
                        </p>
                      )}
                        </>
                      )}
                    </div>
                  );
                })()}

                {/* Keepsake cards toggle */}
                <div className="pt-3 border-t border-[#CAD2C5]/40 space-y-2">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={currentProduct.show_keepsake_cards !== false}
                      onCheckedChange={(v) =>
                        setCurrentProduct({
                          ...currentProduct,
                          show_keepsake_cards: v,
                        })
                      }
                      data-testid="show-keepsake-cards-toggle"
                    />
                    <Label className="text-[#354F52] cursor-pointer">
                      Show the three keepsake cards on this product page
                    </Label>
                  </div>
                  <p className="text-xs text-[#84A98C]">
                    When off, the "What's inside the box" preview with the
                    three printed cards is hidden from customers on this
                    product's page. Turn off for stones, gift vouchers, and
                    anything that doesn't ship with the cards.
                  </p>
                </div>

                <div className="pt-3 border-t border-[#CAD2C5]/40">
                  <p className="text-xs text-[#84A98C] mb-2">
                    Or generate two lifestyle photos for the gallery — one of someone wearing the
                    bracelet, one of her showing it to friends in a sunny Cyprus setting.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGenerateLifestylePair}
                    disabled={generatingAi || generatingLifestyle || uploading || isNewProduct}
                    className="flex items-center gap-2 w-full md:w-auto"
                    data-testid="generate-lifestyle-pair-btn"
                  >
                    {generatingLifestyle ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                        Rendering 2 lifestyle photos…
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                        Add lifestyle photos to gallery
                      </>
                    )}
                  </Button>
                  {isNewProduct && (
                    <p className="text-xs text-[#84A98C] mt-2 italic">
                      Save the product first, then come back to add lifestyle photos.
                    </p>
                  )}
                </div>
              </div>

              {/* Keepsake cards (printable PNGs that ship with each bracelet) */}
              {!isNewProduct && currentProduct.id && (
                <KeepsakeCardsSection
                  productId={currentProduct.id}
                  apiBase={API}
                  backendUrl={BACKEND_URL}
                />
              )}

              {/* Additional Images (Gallery) */}
              <div className="space-y-2">
                <Label className="text-[#354F52] flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" strokeWidth={1.5} />
                  Additional Images (gallery)
                </Label>
                <p className="text-xs text-[#84A98C]">
                  Shown as thumbnails on the product page · click any to set as cover
                </p>

                <div className="flex items-center gap-3">
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                    multiple
                    onChange={handleGalleryUpload}
                    className="hidden"
                    data-testid="gallery-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => galleryInputRef.current?.click()}
                    disabled={uploading}
                    className="flex items-center gap-2"
                    data-testid="gallery-upload-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" strokeWidth={1.5} />
                        Add gallery images
                      </>
                    )}
                  </Button>
                  <span className="text-xs text-[#84A98C]">
                    {(currentProduct.images || []).length} extra{(currentProduct.images || []).length === 1 ? '' : 's'}
                  </span>
                </div>

                {(currentProduct.images || []).length > 0 && (
                  <div
                    className="grid grid-cols-3 md:grid-cols-5 gap-3 mt-3"
                    data-testid="gallery-list"
                  >
                    {currentProduct.images.map((img, idx) => (
                      <div
                        key={`${img}-${idx}`}
                        className="relative group aspect-square rounded-sm overflow-hidden bg-[#F5F5F0] border border-[#CAD2C5]"
                        data-testid={`gallery-item-${idx}`}
                      >
                        <img
                          src={resolveImageUrl(img)}
                          alt={`Extra ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {aiKindForImage(img) && (
                          <span
                            className="absolute top-1 left-1 text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#354F52]/85 text-[#FAF9F6] flex items-center gap-0.5 z-10"
                            title={`AI-generated · ${aiKindForImage(img)}`}
                            data-testid={`ai-badge-gallery-${idx}`}
                          >
                            <Sparkles className="w-2.5 h-2.5" strokeWidth={2} />
                            AI
                          </span>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => promoteToCover(idx)}
                            className="text-xs text-white bg-[#D4AF37] px-2 py-1 rounded-full hover:bg-[#D4AF37]/90"
                            data-testid={`promote-${idx}`}
                          >
                            Set as cover
                          </button>
                          <button
                            type="button"
                            onClick={() => removeGalleryImage(idx)}
                            className="text-xs text-white bg-[#BC4749] px-2 py-1 rounded-full hover:bg-[#BC4749]/90 flex items-center gap-1"
                            data-testid={`remove-gallery-${idx}`}
                          >
                            <X className="w-3 h-3" strokeWidth={2} />
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-[#354F52]">
                  Short Description *
                </Label>
                <Textarea
                  id="description"
                  value={currentProduct.description}
                  onChange={(e) =>
                    setCurrentProduct({ ...currentProduct, description: e.target.value })
                  }
                  rows={3}
                  placeholder="One-line summary used on product cards & search results"
                  data-testid="input-description"
                />
              </div>

              {/* Three notes */}
              <div className="space-y-5 border-t border-[#CAD2C5]/40 pt-5">
                <p className="text-caption text-[#84A98C]">
                  The three notes that ship with every bracelet
                </p>

                <div className="space-y-2">
                  <Label htmlFor="inspiration" className="text-[#354F52]">
                    1. Inspiration
                  </Label>
                  <p className="text-xs text-[#84A98C]">
                    The story / mantra behind the Morse code message
                  </p>
                  <Textarea
                    id="inspiration"
                    value={currentProduct.inspiration || ''}
                    onChange={(e) =>
                      setCurrentProduct({ ...currentProduct, inspiration: e.target.value })
                    }
                    rows={5}
                    placeholder='"Peace begins the moment you choose not to allow..." Tell the story behind this design.'
                    data-testid="input-inspiration"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="product_description" className="text-[#354F52]">
                    2. Product Description
                  </Label>
                  <p className="text-xs text-[#84A98C]">
                    Materials, detail, closure — the practical specs
                  </p>
                  <Textarea
                    id="product_description"
                    value={currentProduct.product_description || ''}
                    onChange={(e) =>
                      setCurrentProduct({ ...currentProduct, product_description: e.target.value })
                    }
                    rows={5}
                    placeholder="Materials, design detail, closure type, sizing..."
                    data-testid="input-product-description"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gift_message" className="text-[#354F52]">
                    3. Gift Message
                  </Label>
                  <p className="text-xs text-[#84A98C]">
                    Printed on the keepsake card that ships in the box. Use &ldquo;To: ___&rdquo; / &ldquo;From: ___&rdquo; for fill-in lines.
                  </p>
                  <Textarea
                    id="gift_message"
                    value={currentProduct.gift_message || ''}
                    onChange={(e) =>
                      setCurrentProduct({ ...currentProduct, gift_message: e.target.value })
                    }
                    rows={6}
                    placeholder="To: ____________________ &#10;&#10;I give you this bracelet...&#10;&#10;From: ____________________"
                    data-testid="input-gift-message"
                  />
                </div>
              </div>

              {/* Occasions (multi-select) */}
              <div className="space-y-2">
                <Label className="text-[#354F52]">Occasions</Label>
                <p className="text-xs text-[#84A98C]">
                  Tag this bracelet so it appears in matching collections (Wedding Gifts, Birthdays, etc.)
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {OCCASION_OPTIONS.map((occ) => {
                    const active = (currentProduct.occasions || []).includes(occ.id);
                    return (
                      <button
                        key={occ.id}
                        type="button"
                        onClick={() => toggleOccasion(occ.id)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                          active
                            ? 'bg-[#354F52] text-[#FAF9F6] border-[#354F52]'
                            : 'bg-[#FAF9F6] text-[#52796F] border-[#CAD2C5] hover:border-[#354F52]'
                        }`}
                        data-testid={`occasion-toggle-${occ.id}`}
                      >
                        {occ.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Featured toggle */}
              <div className="flex items-center justify-between p-4 bg-[#F5F5F0] rounded-sm">
                <div>
                  <Label htmlFor="featured" className="text-[#354F52] font-medium">
                    Featured
                  </Label>
                  <p className="text-xs text-[#84A98C] mt-1">
                    Highlighted with a &ldquo;Bestseller&rdquo; badge on the shop
                  </p>
                </div>
                <Switch
                  id="featured"
                  checked={currentProduct.featured}
                  onCheckedChange={(checked) =>
                    setCurrentProduct({ ...currentProduct, featured: checked })
                  }
                  data-testid="switch-featured"
                />
              </div>

              {/* Action buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-[#CAD2C5]/30">
                <Button
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                  disabled={saving}
                  data-testid="cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-primary"
                  data-testid="save-btn"
                >
                  {saving ? 'Saving...' : isNewProduct ? 'Create Product' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this product?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently remove <strong>{deleteId}</strong> from your shop.
                Customers will no longer be able to view or buy it. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-[#BC4749] hover:bg-[#BC4749]/90"
                data-testid="confirm-delete"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reviews management dialog */}
        <ReviewsDialog
          product={reviewsDialogProduct}
          open={!!reviewsDialogProduct}
          onOpenChange={(o) => !o && setReviewsDialogProduct(null)}
          onChanged={fetchReviewCounts}
        />
      </div>
    </div>
  );
}
